import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Form, FloatingLabel } from "react-bootstrap";
import { AshaBlockServices } from "../../../services/AshaBlocks";
import LoadingModal from "../../components/LoadingModal.js";
import AlertModal from "../../../components/AlertModal.js";
import styles from "../../../styles/Modal.module.css";

function EditAshaBlock({ show, onHide, ashaBlock, taluks = [], phcs = [], onUpdated }) {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    ashaBlock: ashaBlock?.ashaBlock || "",
    phcId: ashaBlock?.phcId || "",
    talukId: "",
    id: ashaBlock?.id || "",
  });

  const [error, setError] = useState({
    ashaBlock: "",
    phcId: "",
    talukId: "",
    id: ""
  });

  const [alertData, setAlertData] = useState({
    show: false,
    title: "",
    message: "",
    variant: "success",
  });

  // Filter PHCs based on talukId
  const filteredPhcs = phcs.filter((p) => p.talukId === parseInt(formData.talukId));

  // Validation
  const validate = () => {
    let tempError = { ashaBlock: "", phcId: "", talukId: "", id: "" };
    let valid = true;

    if (!formData.ashaBlock.trim()) {
      tempError.ashaBlock = "Asha Block is required";
      valid = false;
    }
    if (!formData.phcId) {
      tempError.phcId = "PHC is required";
      valid = false;
    }
    if (!formData.talukId) {
      tempError.talukId = "Taluk is required";
      valid = false;
    }

    setError(tempError);
    return valid;
  };


const handleSave = async () => {
  if (!validate()) return;

  setLoading(true);
  try {
    const res = await AshaBlockServices.updateAshaBlock(ashaBlock.id, formData);
    if (res.status === 200) {
      onHide(); // ✅ Close modal first
      setAlertData({
        show: true,
        title: "Success",
        message: res.message || "Asha Block updated successfully!",
        variant: "success",
      });
      onUpdated && onUpdated();
    } else {
      throw new Error(res.message || "Error while updating Asha Block");
    }
  } catch (error) {
    setAlertData({
      show: true,
      title: "Error",
      message: error.message || "Failed to update Asha Block",
      variant: "danger",
    });
  } finally {
    setLoading(false);
  }
};

  const handleAlertClose = () => {
    setAlertData((prev) => ({ ...prev, show: false }));
  };

  // Pre-fill form when modal opens
  useEffect(() => {
    if (show && ashaBlock) {
      setFormData({
        id: ashaBlock.id || "",
        ashaBlock: ashaBlock.ashaBlock || "",
        phcId: ashaBlock.phcId || "",
        talukId: ashaBlock.phcDetails?.talukId || "",
      });
    }
    if (!show) {
      // Reset when modal closes
      setFormData({ id: "", ashaBlock: "", phcId: "", talukId: "" });
      setError({ ashaBlock: "", phcId: "", talukId: "", id: "" });
    }
  }, [show, ashaBlock]);

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="md" className={styles.modalShow}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <i className="fas fa-edit me-2"></i>
            Edit Asha Block
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.formGroup}>
            <FloatingLabel controlId="Taluk" label="Taluk*">
              <Form.Select
                value={formData.talukId}
                onChange={(e) =>
                  setFormData({ ...formData, talukId: e.target.value, phcId: "" })
                }
                isInvalid={!!error.talukId}
              >
                <option value="">Select Taluk</option>
                {taluks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.taluk}
                  </option>
                ))}
              </Form.Select>
              {error.talukId && (
                <div className="invalid-feedback d-block">{error.talukId}</div>
              )}
            </FloatingLabel>
          </div>

          <div className={styles.formGroup}>
            <FloatingLabel controlId="phc" label="PHC*">
              <Form.Select
                value={formData.phcId}
                onChange={(e) => setFormData({ ...formData, phcId: e.target.value })}
                isInvalid={!!error.phcId}
              >
                <option value="">Select PHC</option>
                {filteredPhcs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.phc}
                  </option>
                ))}
              </Form.Select>
              {error.phcId && (
                <div className="invalid-feedback d-block">{error.phcId}</div>
              )}
            </FloatingLabel>
          </div>

          <div className={styles.formGroup}>
            <FloatingLabel controlId="ashaBlock" label="Asha Block*">
              <Form.Control
                type="text"
                placeholder="Enter Asha Block"
                value={formData.ashaBlock}
                onChange={(e) => setFormData({ ...formData, ashaBlock: e.target.value })}
                isInvalid={!!error.ashaBlock}
              />
              <Form.Control.Feedback type="invalid">
                {error.ashaBlock}
              </Form.Control.Feedback>
            </FloatingLabel>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="success"
            onClick={handleSave}
            className={styles.primaryButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-1"></i>
                Save Changes
              </>
            )}
          </Button>
          <Button
            variant="danger"
            onClick={onHide}
            className={styles.secondaryButton}
          >
            <i className="fas fa-times me-1"></i>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <LoadingModal show={loading} />

      <AlertModal
        show={alertData.show}
        onHide={handleAlertClose}
        title={alertData.title}
        message={alertData.message}
        variant={alertData.variant}
      />
    </>
  );
}

export default EditAshaBlock;
